import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, Minus, RotateCcw, Send, ExternalLink, PanelRightClose } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useOrg } from './OrgSelector';
import { isUserInternalForOrg } from '../lib/roles';
import { envoyerMessageAccueil } from '../lib/accueilIaApi';

/**
 * Assistante d'accueil Atelier Météris — chat écrit de RODAGE (20/10/2026).
 *
 * Réservé aux techniciens INTERNES : compte affiché (`effectiveUser`, pour qu'un aperçu « comme un client »
 * ne le montre pas), interne pour l'organisation active ET permission `accueil_ia.use`. Le serveur refuse de
 * toute façon tout le reste (préfixe interne + permission) : ce filtre n'est que l'affichage.
 * Texte brut uniquement : les réponses s'affichent telles quelles, jamais interprétées comme du HTML.
 *
 * Depuis le 28/10/2026 (demande William : la bulle flottante gênait), l'assistante s'ouvre depuis le RAIL
 * DROIT (RailDroit.js) : ce module fournit le filtre d'affichage (`useAccueilIaAutorise`) et le panneau.
 */
/** Compte AFFICHÉ (effectiveUser : un aperçu « comme un client » compte comme client) interne pour l'org active. */
export function useCompteInterneAffiche() {
  const { effectiveUser } = useAuth();
  const { activeOrg } = useOrg() || {};
  return isUserInternalForOrg(effectiveUser, activeOrg?.code);
}

export function useAccueilIaAutorise() {
  const { hasPermission } = useAuth();
  const { activeOrg } = useOrg() || {};
  return useCompteInterneAffiche() && hasPermission('accueil_ia.use', activeOrg?.code);
}

const MESSAGES_D_ERREUR = {
  429: null, // le serveur dit quelle limite est atteinte : on affiche son texte
  424: "L'assistante ne répond pas pour le moment, réessayez dans un instant.",
  404: 'Cette conversation n’est plus disponible : ouvrez-en une nouvelle.',
  403: 'Accès refusé.',
  422: 'Message vide ou trop long (1 000 caractères au plus).',
};

export function messageErreur(err) {
  const fixe = MESSAGES_D_ERREUR[err?.status];
  if (fixe) return fixe;
  return err?.message || 'Erreur inattendue.';
}

const BOUTON_ENTETE = {
  background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', padding: 2, display: 'flex',
};

/**
 * Panneau de l'assistante, ouvert depuis le rail droit. Il reste MONTÉ quand il est réduit (`ouvert` faux) :
 * la conversation en cours est gardée. Le rail décide de sa place (`placement`, accolé au rail ou fenêtre
 * détachée) ; le panneau ne fait que l'appliquer. Échap le réduit.
 */
export function PanneauAccueilIa({
  ouvert, onReduire, placement, detache = false, onDetacher, onRattacher, onPoigneeMouseDown,
}) {
  const champ = useRef(null);
  const [conversation, setConversation] = useState(null);
  const [saisie, setSaisie] = useState('');
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  const [erreur, setErreur] = useState('');
  const verrou = useRef(false);
  const basDeListe = useRef(null);
  const echanges = conversation?.echanges || [];

  useEffect(() => {
    if (basDeListe.current && typeof basDeListe.current.scrollIntoView === 'function') {
      basDeListe.current.scrollIntoView({ block: 'end' });
    }
  }, [echanges.length, envoiEnCours]);

  async function envoyer() {
    const texte = saisie.trim();
    if (!texte || verrou.current) return;
    verrou.current = true; // verrou synchrone : un double clic n'envoie pas deux fois
    setEnvoiEnCours(true);
    setErreur('');
    try {
      const data = await envoyerMessageAccueil(conversation?.id, texte);
      setConversation(data.conversation);
      setSaisie('');
    } catch (err) {
      setErreur(messageErreur(err));
    } finally {
      verrou.current = false;
      setEnvoiEnCours(false);
    }
  }

  function nouvelle() {
    setConversation(null);
    setSaisie('');
    setErreur('');
  }

  // Ouverture : le curseur va dans le champ (Échap, Entrée marchent tout de suite au clavier).
  useEffect(() => {
    if (ouvert && champ.current) champ.current.focus();
  }, [ouvert]);

  if (!ouvert) return null;

  return (
    <div
      role="dialog"
      aria-label="Assistante d'accueil"
      data-testid="accueil-ia-panneau"
      data-mode={detache ? 'detache' : 'accole'}
      onKeyDown={(ev) => {
        if (ev.key === 'Escape') {
          ev.stopPropagation();
          onReduire?.();
        }
      }}
      style={{
        position: 'fixed', zIndex: 95, width: 380, maxWidth: 'calc(100vw - 40px)',
        height: 540, maxHeight: 'calc(100vh - 24px)', background: '#fff', borderRadius: 12,
        boxShadow: '0 8px 30px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column', overflow: 'hidden',
        ...placement,
      }}
    >
      {/* En-tête : poignée de déplacement quand la fenêtre est détachée */}
      <div
        data-testid="accueil-ia-entete"
        onMouseDown={detache ? onPoigneeMouseDown : undefined}
        title={detache ? 'Glisser pour déplacer la fenêtre' : undefined}
        style={{
          background: '#0f766e', color: '#fff', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8,
          cursor: detache ? 'move' : 'default', userSelect: 'none',
        }}
      >
        <MessageCircle size={18} />
        <div style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>Assistante d'accueil — test</div>
        <button type="button" onClick={nouvelle} title="Nouvelle conversation" aria-label="Nouvelle conversation"
          style={BOUTON_ENTETE}>
          <RotateCcw size={16} />
        </button>
        {detache ? (
          <button type="button" onClick={onRattacher} title="Rattacher au rail" aria-label="Rattacher au rail"
            style={BOUTON_ENTETE}>
            <PanelRightClose size={16} />
          </button>
        ) : (
          <button type="button" onClick={onDetacher} title="Détacher en fenêtre déplaçable" aria-label="Détacher la fenêtre"
            style={BOUTON_ENTETE}>
            <ExternalLink size={16} />
          </button>
        )}
        <button type="button" onClick={onReduire} title="Réduire (la conversation est gardée) — Échap"
          aria-label="Réduire l'assistante" style={BOUTON_ENTETE}>
          <Minus size={18} />
        </button>
      </div>
      <div style={{ background: '#f0fdfa', color: '#115e59', fontSize: 12, padding: '6px 12px' }}>
        Version de rodage réservée aux techniciens : parlez-lui comme un client. Les conversations sont gardées pour
        améliorer l'assistante.
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {echanges.length === 0 && (
          <div style={{ color: '#6b7280', fontSize: 13 }}>
            Exemple : « Mon PC ne démarre plus, c'est combien ? » ou « Vous êtes ouverts samedi ? »
          </div>
        )}
        {echanges.map((e, i) => (
          <div
            key={i}
            data-testid={`echange-${e.role}`}
            style={{
              alignSelf: e.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%', whiteSpace: 'pre-wrap',
              background: e.role === 'user' ? '#e0f2fe' : '#f3f4f6', borderRadius: 10, padding: '8px 10px', fontSize: 13,
            }}
          >
            {e.texte}
          </div>
        ))}
        {envoiEnCours && <div style={{ color: '#6b7280', fontSize: 13 }}>L'assistante écrit…</div>}
        <div ref={basDeListe} />
      </div>
      {erreur && <div role="alert" style={{ color: '#b91c1c', fontSize: 12, padding: '0 12px 6px' }}>{erreur}</div>}
      {conversation && (
        <div style={{ color: '#9ca3af', fontSize: 11, padding: '0 12px 4px' }}>
          {conversation.tours_restants} message(s) restant(s) dans cette conversation
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, padding: 10, borderTop: '1px solid #e5e7eb' }}>
        <textarea
          ref={champ}
          value={saisie}
          onChange={(ev) => setSaisie(ev.target.value)}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter' && !ev.shiftKey) {
              ev.preventDefault();
              envoyer();
            }
          }}
          maxLength={1000}
          rows={2}
          placeholder="Écrivez comme un client…"
          aria-label="Votre message"
          disabled={envoiEnCours}
          style={{ flex: 1, resize: 'none', border: '1px solid #d1d5db', borderRadius: 8, padding: 8, fontSize: 13, fontFamily: 'inherit' }}
        />
        <button type="button" onClick={envoyer} disabled={envoiEnCours || !saisie.trim()} aria-label="Envoyer"
          style={{ border: 'none', borderRadius: 8, background: '#0f766e', color: '#fff', width: 44, cursor: 'pointer', opacity: envoiEnCours || !saisie.trim() ? 0.5 : 1 }}>
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}
